'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ActionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  loading?: boolean;
  size?: 'default' | 'sm' | 'lg';
}

/**
 * ActionButton - кнопка действия
 * 
 * - Оранжевая (#F46922) когда активна (форма валидна)
 * - Серая (#CCCCCC) когда disabled (форма не валидна)
 * - Без теней, плоский дизайн
 */
const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  ({ className, asChild = false, loading, size = 'default', children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    const sizeClasses = {
      default: 'h-12 px-6 text-base',
      sm: 'h-10 px-4 text-sm',
      lg: 'h-14 px-8 text-lg',
    };

    return (
      <Comp
        className={cn(
          // Базовые стили
          'inline-flex items-center justify-center gap-2 whitespace-nowrap',
          'rounded-lg font-semibold text-white',
          'transition-colors duration-150',
          // Touch feedback
          'active:scale-[0.98]',
          // Focus - яркое состояние для навигации с клавиатуры
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F46922] focus-visible:ring-offset-2',
          // Размер
          sizeClasses[size],
          // Цвет в зависимости от состояния
          isDisabled
            ? 'bg-[#CCCCCC] cursor-not-allowed'
            : 'bg-[#F46922] hover:bg-[#E05A1B]',
          className
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading && <Loader2 className="h-5 w-5 animate-spin" />}
        {children}
      </Comp>
    );
  }
);
ActionButton.displayName = 'ActionButton';

export { ActionButton };




